/**
 * Whether this concept is holding, and the dates behind that —
 * ROUTE_INVENTORY §2.10 #42.
 *
 * ## It leads with the answer now
 *
 * It used to be three rows of raw timings and nothing else: last reviewed,
 * next review, total reviews. Every one of those is a fact the reader has to
 * do arithmetic on to answer the question they actually arrived with, which is
 * "do I still know this, and should I do something about it?". The numbers
 * stay — they are the evidence — but a sentence answers first.
 *
 * The state uses `memoryTier` and `legendLabel`, the **same functions the
 * canvas and its legend use**, so the words here and the dot on the graph can
 * never disagree: a concept drawn amber reads "Worth a review" in both places,
 * and the legend in the corner of the graph is the key to this panel too.
 *
 * ## The branch that keeps a schedule honest
 *
 * **"Next review" reads "After first review" rather than a date when the
 * concept has never been reviewed.** FSRS does produce a `next_review_at` for
 * an unseen concept, and showing it would be a schedule the user has no way to
 * interpret — a date derived from no evidence, presented with the same
 * authority as one derived from four gradings. The branch is on
 * `review_count`, not on the timestamp being null, which matters: the
 * timestamp is usually present.
 *
 * The stability bar that used to sit above these rows was removed. It rendered
 * an empty bar and a dash for every unreviewed concept, which users read as a
 * loading failure rather than as "no data yet".
 *
 * ## No mastery row
 *
 * It used to end with "From quizzes", a `deriveMastery` badge. The label was
 * wrong (once a concept has been reviewed the badge ignores quizzes and reads
 * the review history) and the answer overlapped the state above it, so the
 * same section gave two verdicts on one question. The state is the one the
 * map draws; it is the only verdict here now.
 */

import type { ConceptDetail } from '@/types/mindspace-concept';
import {
  isEstimated,
  legendLabel,
  memoryTier,
  retrievability,
  type MemoryTier,
} from '@/lib/mindspace/memoryTier';
import { formatRelativeTime, formatTimeUntil } from '@/utils/mindspaceDisplay';
import { useIsDarkMode } from '@/hooks/ui/themeClass';
import { canvasPalette } from '@/components/features/mindspace/graph/graphPalette';

/**
 * What each state means in a sentence, in the second person.
 *
 * The NAME of the state is the legend's (`legendLabel`), so the panel and the
 * map never disagree; this is the line under it that explains it.
 *
 * Guilt-free by product principle: a concept you are forgetting is an
 * invitation, never a failure, so nothing here scolds and nothing is red.
 */
const TIER_EXPLANATION: Record<MemoryTier, string> = {
  strong: 'You studied this and it is holding. Nothing to do for now.',
  fresh: 'You learned this in the last week, so it is still fresh.',
  fading:
    'This one is starting to slip. A couple of minutes now saves relearning it later.',
  due: 'Enough time has passed that this is worth coming back to.',
  new: 'A lesson added this to your archive. You have not studied it yet.',
};

/**
 * Studied in a lesson, never reviewed.
 *
 * `memoryTier` returns `strong` here, deliberately: it refuses to invent
 * urgency from data nobody measured. The map draws it as Learned, so the
 * panel calls it Learned too, and the explanation is where the honesty goes:
 * there is no recall estimate yet, because that needs a stability and an
 * elapsed time and this concept has neither.
 */
/**
 * Said after the state when recall is estimated (`isEstimated`): studied,
 * never reviewed, so the figure is an assumption decayed from when it was
 * last studied, and the student should know it is not a measurement.
 */
const ESTIMATED_NOTE =
  'This is an estimate from when you last studied it, because it has not been reviewed yet. Your first review will measure it properly.';

const NOT_YET_REVIEWED =
  'You studied this in a lesson. It has not come up for review yet, so there is nothing to suggest it is slipping. Your first review will tell us how well it has stuck.';

export function MemoryHealthCard({
  concept,
  mapTier,
}: {
  concept: ConceptDetail;
  /**
   * The state the map drew this concept in. When given it wins, so the dot
   * on the map and the words here are always the same answer; the local
   * computation is only the fallback for a panel opened without a map.
   */
  mapTier?: MemoryTier;
}) {
  const isDark = useIsDarkMode();
  const reviews = concept.review_count ?? 0;
  const hasReviewed = reviews > 0;
  const input = {
    stability: concept.stability ?? null,
    last_reviewed_at: concept.last_reviewed_at ?? null,
    created_at: null,
    evidence_status: concept.evidence_status ?? null,
    review_count: concept.review_count ?? null,
    last_studied_at: concept.last_studied_at ?? null,
    estimated_stability: concept.estimated_stability ?? null,
  };
  const tier = mapTier ?? memoryTier(input);
  // `strong` with nothing behind it: studied in a lesson, never reviewed, so
  // there is no retrievability to report. See `NOT_YET_REVIEWED`.
  // Only ever said of a calm state: a map tier of fading or due was measured,
  // even when this endpoint lacks the `created_at` it was measured from.
  const unmeasured =
    (tier === 'strong' || tier === 'fresh') && retrievability(input) == null;
  const estimated = isEstimated(input);
  const recall = tier === 'new' ? null : retrievability(input);
  const explanation = unmeasured
    ? NOT_YET_REVIEWED
    : estimated
      ? `${TIER_EXPLANATION[tier]} ${ESTIMATED_NOTE}`
      : TIER_EXPLANATION[tier];

  return (
    <section>
      <h4 className='text-sm font-medium text-foreground'>Memory</h4>
      <p className='mb-2 mt-0.5 text-xs text-muted-foreground'>
        Estimated from how long it has been since you last saw this, and how
        well it has stuck so far.
      </p>

      {/* The same swatch and the same name the map uses for this concept,
          from the same functions, so a reader can carry a colour from the
          map to this panel and back. It is why the graph's legend doubles as
          this section's key. */}
      <p className='flex items-center gap-2 text-sm font-medium text-foreground'>
        <span
          aria-hidden
          className='h-2.5 w-2.5 flex-shrink-0 rounded-full'
          style={{ backgroundColor: canvasPalette(isDark).nodeTier(tier) }}
        />
        {legendLabel(tier)}
      </p>
      <p className='mb-3 mt-0.5 text-xs leading-relaxed text-muted-foreground'>
        {explanation}
      </p>

      <dl className='space-y-1.5 border-t border-border pt-3'>
        <TimingRow
          label='Last reviewed'
          value={formatRelativeTime(concept.last_reviewed_at ?? null)}
        />
        <TimingRow
          label='Next review'
          value={
            hasReviewed
              ? formatTimeUntil(concept.next_review_at ?? null)
              : 'After first review'
          }
        />
        <TimingRow label='Times reviewed' value={String(reviews)} />
        {recall != null && (
          <TimingRow
            label={
              estimated
                ? "Chance you'd recall it (est.)"
                : "Chance you'd recall it"
            }
            value={`${estimated ? '~' : ''}${Math.round(recall * 100)}%`}
          />
        )}
      </dl>
    </section>
  );
}

function TimingRow({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex items-baseline justify-between gap-2'>
      <dt className='text-xs text-muted-foreground'>{label}</dt>
      <dd className='text-sm font-medium tabular-nums text-foreground'>
        {value}
      </dd>
    </div>
  );
}
